import React, { useState, createContext, useContext } from 'react';
import useDialogState from '../../../hooks/use-dialog-state'; // Adjusted path
import type { UserProfile } from '@/lib/types/core';

// Extended dialog types
type UsersDialogType = 'add' | 'edit' | 'delete' | 'activate'; // Added 'activate'

// Define the context type
interface UsersContextType {
  dialogOpen: UsersDialogType | null;
  setDialogOpen: (_type: UsersDialogType | null, _user?: UserProfile) => void; // Added optional user
  currentUser: UserProfile | null; // For general edit/view purposes
  setCurrentUser: React.Dispatch<React.SetStateAction<UserProfile | null>>;
  userToMutate: UserProfile | null; // Specifically for actions like delete/activate
  // isMutatingUser state will be managed by the component performing the mutation (e.g., users/index.tsx)
  // as it involves async calls and toast notifications tied to that component's lifecycle.
}

// Create the context
const UsersContext = createContext<UsersContextType | null>(null);

interface UsersProviderProps {
  children: React.ReactNode;
}

// Create the provider component
export default function UsersProvider({ children }: UsersProviderProps) {
  const [rawDialogOpen, setRawDialogOpen] = useDialogState<UsersDialogType>(null);
  const [currentUser, setCurrentUser_orig] = useState<UserProfile | null>(null); // Renamed original setCurrentUser
  const [userToMutate, setUserToMutate] = useState<UserProfile | null>(null);

  // Wrapped setCurrentUser for logging
  const setCurrentUser = (userOrFn: React.SetStateAction<UserProfile | null>) => {
    // setCurrentUser called
    setCurrentUser_orig(userOrFn);
  };

  const setDialogOpen = (type: UsersDialogType | null, user?: UserProfile) => {
    // setDialogOpen called
    
    if (type === 'edit') {
        // Setting currentUser for edit
        setCurrentUser(user || null); 
        setUserToMutate(null); // Clear userToMutate if opening edit dialog
        // Add a delay to ensure state has settled before opening dialog
        setTimeout(() => {
          setRawDialogOpen(type);
          // rawDialogOpen state updated
        }, 200);
    } else if (type === 'delete' || type === 'activate') {
        setUserToMutate(user || null);
        setCurrentUser(null); // Clear currentUser if opening delete/activate
        setRawDialogOpen(type);
    } else if (type === null) { // Dialog is closing
        setCurrentUser(null);
        setUserToMutate(null);
        setRawDialogOpen(type);
    } else {
        // For 'add', currentUser and userToMutate are typically not needed or are cleared
        setRawDialogOpen(type);
    }
  };

  return (
    <UsersContext.Provider value={{ 
        dialogOpen: rawDialogOpen, 
        setDialogOpen, 
        currentUser, 
        setCurrentUser, 
        userToMutate 
    }}>
      {children}
    </UsersContext.Provider>
  );
}

// Create the custom hook to use the context
export const useUsers = () => {
  const context = useContext(UsersContext);

  if (!context) {
    throw new Error('useUsers must be used within a UsersProvider');
  }

  return context;
};
